import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { apiPost } from "../../api";
import { spaDataCacheClearAll } from "../../spaDataCache";
import { appRoutes } from "../../routes";
import { EmptyState } from "../../components/EmptyState";
import { Shell } from "../../layout/Shell";
import { SessionProvider } from "../../sessionContext";
import { modbusDeviceShareViewerSearch } from "./modbusDeviceShareViewerSearch";
import { modbusDeviceViewTokenGuestSearch } from "./modbusDeviceViewTokenGuestSearch";
import { modbusDeviceViewStandaloneSearch } from "./modbusDeviceViewStandaloneSearch";
import { extractModbusSharePublicToken } from "./extractModbusSharePublicToken";
import { ModbusSharePublicHandshake } from "./ModbusSharePublicHandshake";
import { useSession } from "./useSession";
import { LoginScreen } from "./LoginScreen";
import { ResetPasswordScreen } from "./ResetPasswordScreen";
import { SessionRetry } from "./SessionRetry";
import { Page } from "./Page";

function RouteSuspenseFallback() {
  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">
      <Loader2 className="h-8 w-8 animate-spin" aria-label="Loading" />
    </div>
  );
}

export default function App() {
  const { session, setSession, loading, refresh, connectionIssue } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionProviderValue = { session, setSession, loading, refresh };
  const sharePublicToken =
    typeof location.pathname === "string"
      ? extractModbusSharePublicToken(location.pathname)
      : null;
  const modbusMachineTokenGuest =
    location.pathname === "/modbus-device-view/machine" && modbusDeviceViewTokenGuestSearch(location.search);
  const modbusStandalone =
    (location.pathname === "/modbus-device-view" && modbusDeviceViewStandaloneSearch(location.search)) ||
    modbusMachineTokenGuest;
  const modbusStandaloneIsShareOnly = modbusDeviceShareViewerSearch(location.search);
  /** Share cookie or device-view token — open live view without console login. */
  const modbusStandaloneGuestOk = modbusStandaloneIsShareOnly || modbusMachineTokenGuest;
  async function logout() {
    try {
      await apiPost("/api/spa/logout", {}, session?.csrfToken);
    } finally {
      spaDataCacheClearAll();
      try {
        await refresh();
      } finally {
        navigate("/login", { replace: true });
      }
    }
  }
  if (sharePublicToken) {
    return <ModbusSharePublicHandshake token={sharePublicToken} search={location.search} />;
  }
  if (modbusStandalone) {
    if (loading) {
      return (
        <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      );
    }
    if (!session?.authenticated && !modbusStandaloneGuestOk) {
      const next = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }
    const route = appRoutes.find((r) =>
      r.id === (modbusMachineTokenGuest ? "modbus_device_view_machine" : "modbus_device_view")
    );
    if (route) {
      return (
        <SessionProvider value={sessionProviderValue}>
          <div className="modbus-device-view-standalone-shell min-h-screen">
            <Suspense fallback={<RouteSuspenseFallback />}>
              <Page key={`${location.pathname}${location.search}`} route={route} />
            </Suspense>
          </div>
        </SessionProvider>
      );
    }
  }
  /* Full refresh clears React state; session is null until /api/spa/session returns. Without this gate
   * the login layout flashes briefly even when the browser still has a valid session cookie. */
  if (loading && session === null && !connectionIssue) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (connectionIssue && !session?.authenticated) {
    return <SessionRetry issue={connectionIssue} onRetry={() => void refresh()} />;
  }
  if (session?.authenticated && location.pathname !== "/login") {
    return (
      <SessionProvider value={sessionProviderValue}>
        {connectionIssue ? (
          <div className="flash error mapping-card" style={{ margin: 0 }}>
            {connectionIssue.title} {connectionIssue.description}{" "}
            <button type="button" className="users-admin-btn-ghost users-admin-btn-inline" onClick={() => void refresh()}>
              Retry
            </button>
          </div>
        ) : null}
        <Shell session={session} onLogout={logout}>
          <Routes>
            {appRoutes.map((route) => (
              <Route
                key={`${route.id}-${route.path}`}
                path={route.path}
                element={<Page key={`${route.id}-${location.pathname}${location.search}`} route={route} />}
              />
            ))}
            <Route path="/mapping" element={<Navigate to="/csu-template?id=default" replace />} />
            <Route path="*" element={<EmptyState title="Page not found" description="Choose a page from the navigation." />} />
          </Routes>
        </Shell>
      </SessionProvider>
    );
  }
  if (location.pathname === "/reset-password") {
    return <ResetPasswordScreen csrfToken={session?.csrfToken} bootstrapSession={refresh} />;
  }
  return (
    <LoginScreen
      csrfToken={session?.csrfToken}
      bootstrapLoading={loading && !session}
      bootstrapSession={refresh}
      onLoggedIn={setSession}
    />
  );
}
